import type { OperationRun } from '@foodlista/trigger-vue'
import type {
  ScopedOperationScope,
} from '../utils/scoped-operation-contract'
import {
  type AllergenDetectInput,
  allergenDetectInputSchema,
  allergenDetectManifest,
  type AllergenDetectOutput,
  allergenDetectOutputSchema,
  type AllergenDetectProgressPhase,
} from '@foodlista/operations'
import { PRODUCTS_CACHE_KEYS } from '~/domains/products/queries'
import { operationPayloadOf } from '../completion/extract'
import { defineScopedOperation } from '../composables/defineScopedOperation'
import { presentAllergenDetect } from '../presentation/presenters'
import { registerOperationPresenter } from '../presentation/registry'
import { registerOperationProgressTranslator } from '../progress/registry'
import { allergenDetectScopePayload } from '../scope/payloads'

export type AllergenDetectionInput = AllergenDetectInput

export interface AllergenDetectionScope extends ScopedOperationScope {
  productId?: string
}

export interface AllergenDetectionResult extends AllergenDetectOutput {
  skipIfAllergensChangedFrom: AllergenDetectInput['skipIfAllergensChangedFrom']
}

export function parseAllergenDetectOutput(
  run: OperationRun,
): AllergenDetectionResult {
  const output = allergenDetectOutputSchema.parse(run.output)
  const payload = operationPayloadOf(run)
  const input = allergenDetectInputSchema.parse(payload?.data)

  return {
    ...output,
    skipIfAllergensChangedFrom: input.skipIfAllergensChangedFrom,
  }
}

registerOperationProgressTranslator(
  allergenDetectManifest.type,
  (phase, t) => {
    const typed = phase as AllergenDetectProgressPhase
    switch (typed.kind) {
      case 'analyzing':
        return t('operations.boc.progress.allergenDetect.analyzing')
      case 'detected':
        return t(
          'operations.boc.progress.allergenDetect.detected',
          { count: typed.count },
          typed.count,
        )
      case 'done':
        return t('operations.boc.progress.allergenDetect.done')
    }
  },
)

registerOperationPresenter(allergenDetectManifest.type, presentAllergenDetect)

export const useAllergenDetectOperation = defineScopedOperation<
  AllergenDetectionInput,
  AllergenDetectionScope,
  AllergenDetectionResult
>({
  manifest: allergenDetectManifest,
  idempotency: { mode: 'request' },
  scopePayload: allergenDetectScopePayload,
  parseOutput: parseAllergenDetectOutput,
  enqueue: (input, { orpc, buildEnqueueInput }) =>
    orpc.ai.detectAllergensAsync(buildEnqueueInput(input)),
  onCompleted: async ({ scope, queryClient }) => {
    if (!scope.productId) {
      return
    }

    await queryClient.invalidateQueries({
      queryKey: PRODUCTS_CACHE_KEYS.byProductId(scope.productId),
    })
  },
})
